import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Avatar } from 'components/Avatar';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { CollapsibleSection } from 'components/Collapsible';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { Pill } from 'components/Pill';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import { rbAddReferencePrefix } from 'lib/riskbase';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { selectTaskDetailsState } from 'store/tasks/selectors';
import {
  GENERIC_KEY_TASK_ASSIGNED_ADDITIONAL_DETAILS_EXPANDED,
  GENERIC_KEY_TASK_ASSIGNED_COMMENTS_EXPANDED,
  GENERIC_KEY_TASK_ASSIGNED_PHOTOS_EXPANDED,
  TaskDetailsResponse,
} from 'types/Tasks';
import { useSelector } from 'hooks/useSelector';
import { PageEmpty } from 'components/PageEmpty';
import { GapSmall } from 'components/GapSmall';
import { Gap } from 'components/Gap';
import { TaskAssignedComment } from './TaskAssignedComment';
import { TaskAssignedCommentComposer } from './TaskAssignedCommentComposer';
import { TaskAssignedPhotosSection } from './TaskAssignedPhotosSection';
import { pluralise } from 'lib/pluralise';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import {
  getDueDateIconColor,
  getDueDateLabel,
  getStatusIconColor,
} from './utilities';
import { useDispatch } from 'react-redux';
import { doTasksAssignedDetailsClose, NAVIGATE_MODAL } from 'store/actions';
import { useTaskSectionExpanded } from './useTaskSectionExpanded';
// import { doTasksAssignedDetailsClose } from 'store/actions';

const EMPTY_VALUE = 'Not Set';

type DetailProps = NativeStackScreenProps<
  NavigationStacks[Stacks.TasksAssigned],
  Screens.TasksAssignedDetail
>;

function formatCurrency(value?: number | null): string {
  if (value === undefined || value === null) {
    return EMPTY_VALUE;
  }
  return `${(value / 100).toFixed(2)}`;
}

function formatHexColour(colour?: string | null): string | undefined {
  if (!colour) {
    return undefined;
  }
  return colour.startsWith('#') ? colour : `#${colour}`;
}

const TaskDetailsContent: React.FC<{
  task: TaskDetailsResponse;
}> = ({ task }) => {
  const dispatch = useDispatch();
  const {
    expanded: additionalDetailsExpanded,
    toggle: handleAdditionalDetailsToggle,
  } = useTaskSectionExpanded(
    GENERIC_KEY_TASK_ASSIGNED_ADDITIONAL_DETAILS_EXPANDED,
  );
  const { expanded: commentsExpanded, toggle: handleCommentsToggle } =
    useTaskSectionExpanded(GENERIC_KEY_TASK_ASSIGNED_COMMENTS_EXPANDED);
  const { expanded: photosExpanded, toggle: handlePhotosToggle } =
    useTaskSectionExpanded(GENERIC_KEY_TASK_ASSIGNED_PHOTOS_EXPANDED);
  const what = task.what || 'No Description';
  const showTitle = !!task.title && task.title !== what;
  const cost = task.cost !== null ? formatCurrency(task.cost) : null;
  const title = task.title.trim();
  const commentCount = task.comments.filter(
    (comment) => comment.comment.trim().length > 0,
  ).length;
  const commentsLabel = pluralise(
    commentCount,
    '{x} Comment',
    '{x} Comments',
    'No Comments',
  );

  const handleStatusPress = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.TasksAssignedStatus,
        params: {
          taskId: task.id,
          activeStatus: task.activeStatus,
          availableStatuses: task.availableStatuses,
        },
      }),
    );
  }, [dispatch, task.activeStatus, task.availableStatuses, task.id]);

  return (
    <Gap>
      <Card>
        <CardRow childrenStyle={{ gap: theme.padding / 2 }}>
          <View>
            {showTitle ? (
              <>
                <Txt weight="bold" size="large">
                  {title}
                </Txt>
                <Txt>{what}</Txt>
              </>
            ) : (
              <Txt weight="bold" size="large">
                {what}
              </Txt>
            )}
          </View>

          <SeparatorHorizontal />

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignContent: 'center',
              columnGap: theme.padding,
              rowGap: theme.padding / 2,
              flexWrap: 'wrap',
              minHeight: 27,
            }}
          >
            <Txt size="xxxsmall" weight="bold" numberOfLines={1}>
              {rbAddReferencePrefix(task.reference)}
              {/* {!!task.customReference && ` • ${task.customReference}`} */}
            </Txt>
            <Pill
              withIcon={true}
              iconSize={8}
              iconColor={getStatusIconColor(task.activeStatus.statusType)}
              label={task.activeStatus.statusName}
              textSize="xxxsmall"
              textWeight="bold"
            />
            <Pill
              withIcon={true}
              iconSize={8}
              iconColor={getDueDateIconColor(task.dueDate)}
              label={getDueDateLabel(task.dueDate)}
              textSize="xxxsmall"
              textWeight="bold"
            />
          </View>
        </CardRow>

        {!!task.why && <CardRow label="Why" labelAlignTop value={task.why} />}
        {!!task.where && (
          <CardRow
            label="Where"
            labelAlignTop
            value={task.where || EMPTY_VALUE}
          />
        )}
      </Card>

      <GapSmall>
        <Card>
          <CardRow childrenStyle={styles.summaryCardContent}>
            <View style={styles.summaryCard}>
              <Avatar icon={ICONS.HouseBuildingLight} data={null} size={40} />
              <View style={styles.summaryText}>
                <Txt weight="bold">{task.property.name}</Txt>
                {!!(
                  task.property.reference || task.property.customReference
                ) && (
                  <View style={styles.assetReference}>
                    <Txt size="xsmall" style={styles.summarySubtitle}>
                      {[
                        task.property.reference &&
                          rbAddReferencePrefix(task.property.reference),
                        task.property.customReference,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </Txt>
                  </View>
                )}
                {!!task.property.address && (
                  <Txt size="xsmall" style={styles.summarySubtitle}>
                    {task.property.address}
                  </Txt>
                )}
              </View>
            </View>
          </CardRow>
        </Card>

        {task.asset && (
          <Card>
            <CardRow childrenStyle={styles.summaryCardContent}>
              <View style={styles.summaryCard}>
                <Avatar icon={ICONS.DiceD6Light} data={null} size={40} />
                <View style={styles.summaryText}>
                  <Txt weight="bold">{task.asset.name}</Txt>
                  {!!task.asset.reference && (
                    <View style={styles.assetReference}>
                      <Txt size="xsmall" style={styles.summarySubtitle}>
                        {rbAddReferencePrefix(task.asset.reference)}
                      </Txt>
                    </View>
                  )}
                  {!!task.asset.locationFields.length && (
                    <Txt size="xsmall" style={styles.summarySubtitle}>
                      {task.asset.locationFields
                        .map(({ label, value }) => `${label}: ${value}`)
                        .join(' • ')}
                    </Txt>
                  )}
                </View>
              </View>
            </CardRow>
          </Card>
        )}

        <Card>
          <CardRow
            icon={ICONS.CircleDotLight}
            iconColor={getStatusIconColor(task.activeStatus.statusType)}
            label="Status"
            onPress={handleStatusPress}
            value={task.activeStatus.statusName}
          />
        </Card>
      </GapSmall>

      {!!task.photos.length && (
        <TaskAssignedPhotosSection
          photos={task.photos}
          expanded={photosExpanded}
          onToggle={handlePhotosToggle}
        />
      )}

      <CollapsibleSection
        expanded={additionalDetailsExpanded}
        label="Additional Details"
        onToggle={handleAdditionalDetailsToggle}
      >
        <Card separator={null}>
          <ViewWithSeparators>
            {!!task.timescaleName && (
              <CardRow
                icon={ICONS.ClockLight}
                label="Timescale"
                childrenStyle={styles.priorityPillContainer}
              >
                <Pill
                  withIcon
                  transparent
                  iconColor={formatHexColour(task.timescaleColor)}
                  label={task.timescaleName}
                />
              </CardRow>
            )}
            {!!task.severityName && (
              <CardRow
                icon={ICONS.ArrowUpShortWideLight}
                label="Severity"
                childrenStyle={styles.priorityPillContainer}
              >
                <Pill
                  withIcon
                  transparent
                  iconColor={formatHexColour(task.severityColor)}
                  label={task.severityName}
                />
              </CardRow>
            )}
            <CardRow
              icon={ICONS.TagLight}
              label="Category"
              value={task.categoryName || EMPTY_VALUE}
            />
            <CardRow
              icon={ICONS.InputNumberDuo}
              label="Quantity"
              value={String(task.quantity)}
            />
            {cost !== null && (
              <CardRow
                icon={ICONS.SterlingSignLight}
                label="Estimated Cost"
                value={cost}
              />
            )}
          </ViewWithSeparators>
        </Card>
      </CollapsibleSection>

      <CollapsibleSection
        expanded={commentsExpanded}
        label={commentsLabel}
        onToggle={handleCommentsToggle}
        unmountOnCollapse={false}
      >
        <GapSmall>
          <TaskAssignedCommentComposer key={task.id} taskId={task.id} />
          {task.comments.length ? (
            task.comments.map((comment) => (
              <TaskAssignedComment
                key={comment.id}
                authorName={comment.author.name}
                authorAvatarUrl={comment.author.avatar}
                createdAt={new Date(comment.createdDate)}
                comment={comment.comment}
                files={comment.files}
                statusChange={comment.statusChange}
              />
            ))
          ) : (
            <Card>
              <CardRow placeholder="No comments added yet..." />
            </Card>
          )}
        </GapSmall>
      </CollapsibleSection>
    </Gap>
  );
};

export const TaskDetailsPanel: React.FC<{
  onPressBackButton?: () => void;
  isMobile: boolean;
}> = ({ onPressBackButton, isMobile }) => {
  const detailsState = useSelector(selectTaskDetailsState);

  const useScrollView =
    !detailsState.error && !detailsState.isLoading && !!detailsState.item;

  return (
    <Page
      scrollView={useScrollView}
      withSizingProvider={isMobile}
      header={
        isMobile && (
          <PageHeader
            title="Task Details"
            onPressBackButton={onPressBackButton}
          />
        )
      }
    >
      {detailsState.error ? (
        // Show error
        <PageEmpty
          icon={ICONS.ExclamationSolid}
          text="Unable to Load Task"
          subtext={detailsState.error}
          style={{ flex: 1, justifyContent: 'center' }}
        />
      ) : detailsState.isLoading ? (
        // Show loader
        <View
          style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
        >
          <ActivityIndicator size={56} />
        </View>
      ) : !detailsState.item ? (
        // Show no task selected
        <PageEmpty
          text="No Task Selected"
          subtext="Select a task from the list to view its details"
        />
      ) : (
        // Show task details
        <TaskDetailsContent task={detailsState.item} />
      )}
    </Page>
  );
};

export const TasksAssignedDetailScreen: React.FC<DetailProps> = ({
  navigation,
}) => {
  const dispatch = useDispatch();
  React.useEffect(() => {
    return () => {
      // Annoyingly, this will be called in dev when making changes locally because
      // of how hot reloading works. This will mean the opened task's details will be
      // lost on each change – at the moment, we'll need to comment this out when developing
      dispatch(doTasksAssignedDetailsClose());
    };
  }, [dispatch]);
  return (
    <TaskDetailsPanel onPressBackButton={navigation.goBack} isMobile={true} />
  );
};

const styles = StyleSheet.create({
  priorityPillContainer: {
    alignItems: 'flex-end',
    flexShrink: 1,
    marginLeft: theme.padding,
  },
  summaryCardContent: {
    flex: 1,
  },
  summaryCard: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.padding,
  },
  summaryText: {
    flex: 1,
    flexShrink: 1,
    justifyContent: 'center',
    minWidth: 0,
  },
  summarySubtitle: {
    color: theme.colors.fieldValueText,
  },
  assetReference: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.paddingHorizontalHalf,
  },
  detailEmptyState: {
    alignItems: 'center',
    flex: 1,
    gap: theme.paddingHorizontalHalf,
    justifyContent: 'center',
    padding: theme.padding * 3,
  },
  detailLoader: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  errorTitle: {
    color: theme.colors.red,
    marginBottom: theme.paddingHorizontalHalf,
  },
  errorText: {
    color: theme.colors.fieldValueText,
  },
});
